import {Input, Space} from "antd";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

const isValidDate = (day, month, year) => {
    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);

    if (!d || !m || !y) return false;
    if (m < 1 || m > 12) return false;
    if (y < 1900 || y > new Date().getFullYear()) return false;

    // Ayın gün sayısı (artık yıl dahil): sonraki ayın 0. günü = bu ayın son günü.
    return d >= 1 && d <= new Date(y, m, 0).getDate();
}

const toValue = (day, month, year) => {
    if (!isValidDate(day, month, year)) return null;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Doğum tarihi girişi — gün / ay / yıl ayrı kutucuklar, elle yazılır.
 * Kutucuk dolunca (2/2/4 hane) odak kendiliğinden sonrakine geçer; odaklanan kutucukta
 * metin varsa tamamı seçilir, böylece üzerine yazmak eskisini siler.
 *
 * Form değeri: 'YYYY-MM-DD' — tarih eksik ya da geçersizse null.
 */
export default function BirthdateInput({value, onChange, disabled})
{
    const {t} = useTranslation();
    const [day, setDay] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    const dayRef = useRef();
    const monthRef = useRef();
    const yearRef = useRef();
    // Kendi yaydığımız son değer; aşağıdaki effect'in yazarken araya girmesini engeller.
    const emittedRef = useRef();

    // Dışarıdan gelen değeri (ör. hasta seçilince otomatik dolan tarih) kutucuklara yansıt.
    // Geri dönen kendi değerimizse dokunma: yoksa '2' yazılır yazılmaz '02'ye tamamlanır
    // ve maxLength dolduğu için ikinci hane girilemez.
    useEffect(() => {
        if (value === emittedRef.current) return;

        const [y = '', m = '', d = ''] = (value || '').split('-');
        setDay(d);
        setMonth(m);
        setYear(y);
    }, [value]);

    const digitsOnly = (text, maxLength) => text.replace(/\D/g, '').slice(0, maxLength);

    const emit = (d, m, y) => {
        emittedRef.current = toValue(d, m, y);
        onChange && onChange(emittedRef.current);
    }

    const selectAll = (e) => {
        if (e.target.value) e.target.select();
    }

    const handleDay = (e) => {
        const next = digitsOnly(e.target.value, 2);
        setDay(next);
        emit(next, month, year);
        if (next.length === 2) monthRef.current?.focus();
    }

    const handleMonth = (e) => {
        const next = digitsOnly(e.target.value, 2);
        setMonth(next);
        emit(day, next, year);
        if (next.length === 2) yearRef.current?.focus();
    }

    const handleYear = (e) => {
        const next = digitsOnly(e.target.value, 4);
        setYear(next);
        emit(day, month, next);
    }

    // Boş kutucukta backspace → bir öncekine dön.
    const backspaceToPrevious = (previousRef) => (e) => {
        if (e.key === 'Backspace' && !e.target.value) previousRef.current?.focus();
    }

    // Tek hane yazılıp çıkıldıysa başına sıfır koy: 7 → 07.
    //
    // Değer state'ten DEĞİL kutunun kendisinden (e.target.value) okunur: ikinci hane girilince
    // odak sonraki kutuya senkron geçtiği için blur, React state'i daha yansımadan tetikleniyor.
    // State'e bakan bir closure o anda hâlâ tek haneyi görür ve '10'u '01'e, '05'i '00'a çevirir.
    const padOnBlur = (setText, emitPadded) => (e) => {
        const text = e.target.value;
        if (text.length !== 1) return;

        const padded = text.padStart(2, '0');
        setText(padded);
        emitPadded(padded);
    }

    const boxStyle = {width: 62, textAlign: 'center'};

    return (
        <Space.Compact>
            <Input
                ref={dayRef}
                style={boxStyle}
                placeholder={t('DD')}
                inputMode={'numeric'}
                maxLength={2}
                disabled={disabled}
                value={day}
                onChange={handleDay}
                onFocus={selectAll}
                onBlur={padOnBlur(setDay, (padded) => emit(padded, month, year))}
            />
            <Input
                ref={monthRef}
                style={boxStyle}
                placeholder={t('MM')}
                inputMode={'numeric'}
                maxLength={2}
                disabled={disabled}
                value={month}
                onChange={handleMonth}
                onFocus={selectAll}
                onBlur={padOnBlur(setMonth, (padded) => emit(day, padded, year))}
                onKeyDown={backspaceToPrevious(dayRef)}
            />
            <Input
                ref={yearRef}
                style={{...boxStyle, width: 80}}
                placeholder={t('YYYY')}
                inputMode={'numeric'}
                maxLength={4}
                disabled={disabled}
                value={year}
                onChange={handleYear}
                onFocus={selectAll}
                onKeyDown={backspaceToPrevious(monthRef)}
            />
        </Space.Compact>
    )
}
